import {ChangeEvent} from 'react';

import styled from 'styled-components';
import {FieldMetaState} from 'react-final-form';

import {flexColumn, flexSpace} from '~/shared/theme/FlexLayout';
import {captionRegular, smallRegular} from '~/shared/theme/typography';
import FormPieInput from '~/shared/components/FormField/FormInput/FormPieInput';
import {OtlRuleDataLimitationName} from '~/shared/store/models';
import {isOtlRuleDataLimitationName} from '~/shared/store/models/MoneycardOtlRuleManagement';
import {createLogger} from '~/shared/logging';
import {getLocalizationService} from '~/shared/services/localisationService';

const logger = createLogger('OtlRuleLimitations');

const Root = styled.div`
  ${flexSpace}
  align-items: center;
`;

const InputContainer = styled.div`
  width: 90px;
  flex-shrink: 0;
`;

const StyledFormPieInput = styled(FormPieInput)`
  direction: ltr;

  ::before {
    content: '₪';
    color: ${({theme}) => theme.colors.contentPlaceholder};
    padding-right: 2px;
  }
`;

const CaptionAndErrorContainer = styled.div`
  ${flexColumn}
  gap: 4px;
`;

const Caption = styled.div`
  ${smallRegular}
  color: ${({theme}) => theme.colors.contentDefault};
`;

const Error = styled.span`
  display: block;
  ${captionRegular}
  color: ${({theme}) => theme.colors.contentError};
`;

type OtlRuleLineProps = {
  ruleName: string;
  inputType: string;
  value: {
    value: string;
  };
  onChange: (payload: {value: string}) => void;
  name: string;
  onLimitChange: (name: OtlRuleDataLimitationName, value: string) => void;
  meta: FieldMetaState<{value: number}>;
};

const OtlRuleLine = ({ruleName, value: formValueObject, onChange, name, onLimitChange, meta, ...inputProps}: OtlRuleLineProps) => {
  const {t} = getLocalizationService();

  const hasError = !meta.active && meta.touched && meta.error;

  return (
    <Root>
      <CaptionAndErrorContainer>
        <Caption>{ruleName}</Caption>
        {hasError && <Error>{t(meta.error)}</Error>}
      </CaptionAndErrorContainer>
      <InputContainer>
        <StyledFormPieInput
          id={`form_input_rule_${ruleName}`}
          type="number"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const {value} = e.target;

            onChange({...formValueObject, value});
            if (!isOtlRuleDataLimitationName(name)) {
              logger.error('Name of the field is not the OtlRuleName');
              return;
            }
            onLimitChange(name, value);
          }}
          value={formValueObject.value}
          removeFloatLabel
          inputPlaceholder="0"
          hasError={hasError}
          {...inputProps}
        />
      </InputContainer>
    </Root>
  );
};

export default OtlRuleLine;
